import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import { Notification, NotificationSearchResponse } from 'axiosApi/models';
import { useEffect, useRef, useContext } from 'react';
import { useWebSocketNotificationService } from 'websocket';
import { useWebSocketNotification } from 'websocket/useNotification';
import { AuthContext } from 'components/authentication/context/authContext';

const NOTIFICATION_KEY = (userEntityId?: number) => ['notifications', userEntityId];
const NOTIFICATION_COUNT_KEY = (userEntityId?: number) => ['notification_count', userEntityId];

export const useNotificationsStorage = (page: number, pageSize: number) => {

  const queryClient = useQueryClient();
  const api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  const { notifications, latestNotification, clearNotifications } = useWebSocketNotification<Notification>('message');
  const {isConnected} = useWebSocketNotificationService();
  const addFromSocketMutation = useAddFromSocketMutation();
  const wasConnected = useRef(isConnected);

  useEffect(() => {
    if (notifications.length > 0 && isConnected) {
      notifications.forEach(notification => {
        addFromSocketMutation.mutate(notification);
      });
       clearNotifications();
    }

    if (wasConnected.current && !isConnected) {
      console.warn("Websocket disconnected — invalidating notification queries...");
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEY(userEntityId) });
    }
      wasConnected.current = isConnected;
  }, [notifications, isConnected, addFromSocketMutation, clearNotifications, userEntityId, queryClient]);


  const query : {  data,
  dataUpdatedAt: number, // timestamp of the last data update
  error: any, // The error object for the query, if an error was thrown.
  failureCount: number,
  isError: boolean, // lo mismo que status === 'error'
  isRefetching: boolean, // isFetching && !isPending
  isLoading: boolean, // si esta cargando la data
  isFetching: boolean, 
  //refetch // refetch the query
} = useQuery({
    queryKey: [...NOTIFICATION_KEY(userEntityId), page, pageSize],
    queryFn: async (): Promise<NotificationSearchResponse> => {
      const res = await api.notificationsApi.apiVversionNotificationSkipAndTakeGet('1', page, pageSize);
      return res?.data?.data ?? { queryResult: [], totalRecords: 0 };
    },
    staleTime: Infinity, // data is fresh forever
    gcTime: Infinity, // no garbage collection
    refetchOnMount: false, 
    refetchOnWindowFocus: true, 
    structuralSharing: true, 
    throwOnError: false, 
    enabled: !!userEntityId, // Only run query if userEntityId exists
    select: (data) => ({
      queryResult: data.queryResult.map(n => ({ ...n })),
      totalRecords: data.totalRecords,
    }),
    placeholderData: () => {
    const prevData = queryClient.getQueryData([...NOTIFICATION_KEY(userEntityId), page - 1]);
    return prevData;
  },  
});
  return {
    ...query,
  };
};

export const useMarkAllAsReadMutation = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useMutation({
    mutationFn: async () => {
      await api.notificationsApi.apiVversionNotificationMarkAllAsReadPut('1'); // hypothetical endpoint
    },
    onMutate: async () => {
      const notificationKey = NOTIFICATION_KEY(userEntityId);
      await queryClient.cancelQueries({ queryKey: notificationKey });

      const previousData = queryClient.getQueryCache().findAll({ queryKey: notificationKey }).map(q => ({
        queryKey: q.queryKey,
        data: queryClient.getQueryData<NotificationSearchResponse>(q.queryKey),
      }));

      queryClient.getQueryCache().findAll({ queryKey: notificationKey }).forEach(q => {
        queryClient.setQueryData<NotificationSearchResponse>(q.queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            queryResult: oldData.queryResult.map(n => ({ ...n, read: '1' })),
            totalRecords: 0,
          };
        });
      });

      return { previousData };
    },
    onError: (_err, _data, context) => {
      context?.previousData?.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
    }
  });
};

export const useMarkListAsReadMutation = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useMutation({
    mutationFn: async (timestamps: string[]) => {
      await api.notificationsApi.apiVversionNotificationMarkListAsReadPut('1', timestamps); 
      return timestamps;
    },
    onMutate: async (timestamps) => {
      const notificationKey = NOTIFICATION_KEY(userEntityId);
      await queryClient.cancelQueries({ queryKey: notificationKey });

      const previousData = queryClient.getQueryCache().findAll({ queryKey: notificationKey }).map(q => ({
        queryKey: q.queryKey,
        data: queryClient.getQueryData<NotificationSearchResponse>(q.queryKey),
      }));

      queryClient.getQueryCache().findAll({ queryKey: notificationKey }).forEach(q => {
        queryClient.setQueryData<NotificationSearchResponse>(q.queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            queryResult: oldData.queryResult.map(n =>
              timestamps.includes(n.timeStamp) ? { ...n, read: '1' } : n
            ),
            totalRecords: Math.max(0, oldData.totalRecords - timestamps.length),
          };
        });
      });

      return { previousData };
    },
    onError: (_err, _timestamps, context) => {
      context?.previousData?.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
    }
  });
};

export const useMarkAsReadMutation = () => {
  const queryClient = useQueryClient();
  const api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

    return useMutation({
      mutationFn: async (timestamp: string) => {
        await api.notificationsApi.apiVversionNotificationMarkItemAsReadPut('1', timestamp);
        return timestamp;
      },
      onMutate: async (timestamp) => {
        const notificationKey = NOTIFICATION_KEY(userEntityId);
        await queryClient.cancelQueries({ queryKey: notificationKey });

        const previousData = queryClient.getQueryCache().findAll({ queryKey: notificationKey }).map(q => ({
          queryKey: q.queryKey,
          data: queryClient.getQueryData<NotificationSearchResponse>(q.queryKey),
        }));

        queryClient.getQueryCache().findAll({ queryKey: notificationKey }).forEach(q => {
          queryClient.setQueryData<NotificationSearchResponse>(q.queryKey, (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              queryResult: oldData.queryResult.map(n =>
                n.timeStamp === timestamp ? { ...n, read: '1' } : n
              ),
              totalRecords: oldData.totalRecords > 0 ? Math.max(0, oldData.totalRecords - 1) : 0,
            };
          });
        });
        return { previousData };
      },
      onError: (_err, _timestamp, context) => {
        context?.previousData?.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    });
};

export const useAddFromSocketMutation = () => {
  const queryClient = useQueryClient();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;
  
  return useMutation({
    mutationFn: async (notification: Notification) => {
      return notification;
    },
    onMutate: async (newNotification: Notification) => {
      const notificationKey = NOTIFICATION_KEY(userEntityId);
      await queryClient.cancelQueries({ queryKey: notificationKey });

      const previousData = queryClient.getQueryCache().findAll({ queryKey: notificationKey }).map(q => ({
        queryKey: q.queryKey,
        data: queryClient.getQueryData<NotificationSearchResponse>(q.queryKey),
      }));

      queryClient.getQueryCache().findAll({ queryKey: notificationKey }).forEach(q => {

        const [, , page, pageSize] = q.queryKey;
        const pageNum = typeof page === 'number' ? page : Number(page);
        const pageSizeNum = typeof pageSize === 'number' ? pageSize : Number(pageSize);

        if (pageNum === 0) {
            queryClient.setQueryData<NotificationSearchResponse>(q.queryKey, (oldData) => {
            if (!oldData) return oldData;
            if (oldData.queryResult.some(n => n.timeStamp === newNotification.timeStamp)) {
              return oldData; 
            }
            const newQueryResult = [newNotification, ...oldData.queryResult];
            return {
              ...oldData,
              queryResult: newQueryResult.slice(0, pageSizeNum), // Slice to maintain page size
              totalRecords: oldData.totalRecords + 1,
              };
            });
        } else {
            queryClient.invalidateQueries({ queryKey: q.queryKey });
        }
      });

      return { previousData };
    },
    onError: (_err, _notification, context) => {
      // Rollback on error
      context?.previousData?.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });
    }
  });
};

export const useNotificationCount = () => {
  const api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  return useQuery({
    queryKey: NOTIFICATION_COUNT_KEY(userEntityId),
    queryFn: async (): Promise<number> => {
      const res = await api.notificationsApi.apiVversionNotificationGetCountGet('1');

      return res?.data?.data?.totalRecords || res?.data?.data?.queryResult.length || 0;
    },
    staleTime: 15 * 60 * 1000, 
    gcTime: 15 * 60 * 1000, 
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    throwOnError: false,
    enabled: !!userEntityId, // Only run query if userEntityId exists
  });
};

export const useNotificationQueryClient = () => {
  const queryClient = useQueryClient();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;
  const refetch = () => queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEY(userEntityId) , exact: false });
  return { refetch };
};